import type { ComponentPropsWithoutRef, ElementType } from "react";

import type { IcIcon } from "@jetblue/kit-icons/types";
import { Badge } from "@jetblue/kit/badge";
import { IcMosaicStatus } from "@jetblue/kit-icons/ic-mosaic-status";
import { cn } from "../../utilities/tailwind";

export const AvatarSizesArray = [
  "extraLarge",
  "large",
  "medium",
  "small",
  "extraSmall",
] as const;
export type AvatarSize = (typeof AvatarSizesArray)[number];
const NEXT_IMAGE_SIZES: Record<
  AvatarSize,
  Record<"height" | "width", number>
> = {
  extraLarge: { height: 96, width: 96 },
  extraSmall: { height: 24, width: 24 },
  large: { height: 64, width: 64 },
  medium: { height: 48, width: 48 },
  small: { height: 32, width: 32 },
};

export type AvatarProperties<C extends ElementType> = {
  /**
   * Accepts an icon as a function (from our icon library, e.g. `IcCheckbox`). **Note:** the `Icon` only displays in the `extraLarge` and `large` sizes.
   */
  Icon?: IcIcon | undefined;
  /**
   * Defaults to be an img component when a `src` property is provided. Alternatively could also use NextImage.
   */
  as?: ElementType | undefined;
  className?: string | undefined;
  /**
   * Label used to describe the `Icon`.
   */
  iconLabel?: string | undefined;
  /**
   * First and last initial will be used on a bright blue background when there is no src property passed.
   */
  initials: string;
  innerClassName?: string | undefined;
  isMosaic?: boolean | undefined;
  /**
   * Name will be used as the alt property for the image and as an aria-label for the initials.
   */
  name: string;
  /**
   * The fixed size variants of the `Avatar`.
   */
  size?: AvatarSize | undefined;
  /**
   * The `src` value used with the `img`.
   */
  source?: string | undefined;
} & ComponentPropsWithoutRef<C>;
export const Avatar = <C extends ElementType>({
  Icon,
  as = "img",
  className = "",
  iconLabel = "",
  initials,
  innerClassName = "",
  isMosaic = false,
  name,
  size = "medium",
  source = "",
}: AvatarProperties<C>) => {
  const isLargerAvatar = size === "extraLarge" || size === "large";
  const ImgComponent = as;

  const twClassName = cn(
    "relative data-[size=extraLarge]:size-24 data-[size=extraSmall]:size-6 data-[size=large]:size-16 data-[size=medium]:size-12 data-[size=small]:size-8",
    className,
  );

  const imgTwClassName = cn(
    "flex size-full items-center justify-center rounded-full border-2 border-outline-variant-lowest bg-brand-accent-2 font-normal text-on-primary data-[size=extraLarge]:text-l-heading/[2rem] data-[size=extraSmall]:text-[0.5rem] data-[size=large]:text-l-heading/[2rem] data-[size=medium]:text-body data-[size=small]:text-legal",
    innerClassName,
  );

  const mosaicTwClassName = cn(
    "absolute",
    {
      "end-1 bottom-1 h-[23px] w-6": size === "extraLarge",
    },
    {
      "end-0.5 bottom-0.5 h-[17px] w-[18px]": size === "large",
    },
    {
      "end-0 bottom-0 h-[17px] w-[18px]": size === "medium",
    },
    {
      "end-0 bottom-0 h-[11px] w-3": size === "small" || size === "extraSmall",
    },
  );

  return (
    <div className={twClassName} data-size={size}>
      {source && (
        <ImgComponent
          alt={name}
          className={imgTwClassName}
          height={NEXT_IMAGE_SIZES[size].height}
          src={source}
          width={NEXT_IMAGE_SIZES[size].width}
        />
      )}

      {!source && (
        <div
          aria-label={name}
          className={imgTwClassName}
          data-size={size}
          role="img"
        >
          {initials}
        </div>
      )}

      {isMosaic && (
        <IcMosaicStatus
          aria-label="With Mosaic status"
          className={mosaicTwClassName}
        />
      )}

      {Icon && isLargerAvatar && (
        <Badge
          className="absolute data-[size=extraLarge]:end-1 data-[size=extraLarge]:bottom-1 data-[size=large]:end-0.5 data-[size=large]:bottom-0.5"
          data-size={size}
        >
          <Icon aria-label={iconLabel} />
        </Badge>
      )}
    </div>
  );
};
Avatar.displayName = "Avatar";
